import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, TextField } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useParams, Link } from 'react-router-dom';

import {
  setCurrentSlide,
  resetEditor,
  copyToClipboard,
  selectElements,
  setActiveTool,
} from './slideEditorSlice';
import { useSlideElements } from './hooks/useSlideElements';
import { useSlideHistory } from './hooks/useSlideHistory';
import {
  useCreateVideoPlaybackRegistry,
  VideoPlaybackRegistryProvider,
} from './components/VideoPlaybackRegistryProvider';
import SlideCanvas from './components/SlideCanvas';
import SlideThumbnailPanel from './components/SlideThumbnailPanel';
import SlideToolbar from './components/SlideToolbar';
import AccessibilityChip from './components/AccessibilityChip';
import AccessibilityPreflightDialog from './components/AccessibilityPreflightDialog';
import ImageUrlDialog from './components/ImageUrlDialog';
import PresentationOverlay from './components/PresentationOverlay';
import { getDefaultElements } from './slideTemplates';
import {
  checkDeck,
  checkSlide,
  type AccessibilityFix,
  type AccessibilityIssue,
} from './utils/accessibilityChecker';
import VideoLibraryDialog from './components/VideoLibraryDialog';
import type { VideoSelection } from './components/VideoLibraryDialog';

import type { AgentContext } from '@/shared/types/chat';
import { asSlideContent, createEmptySlideContent, type Resource } from '@/shared/types/resource';
import { resolveSlideBackground, resolveSlideForChecker } from '@/shared/theming/resolve';
import { registryThemeSnapshot, themesEqual, type ThemeId } from '@/shared/theming/registry';
import { DEFAULT_THEME } from '@/shared/theming/default-theme';
import { SLIDE_PAGE_WIDTH, SLIDE_PAGE_HEIGHT } from '@/shared/types/slide';
import type {
  SlideContentV1,
  SlideTemplate,
  SlideElement,
  ImageElement,
} from '@/shared/types/slide';
import UnsplashImageLibraryDialog from '@/features/resources/components/UnsplashImageLibraryDialog';
import type { UnsplashImageSelection } from '@/features/resources/components/UnsplashImageLibraryDialog';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/redux';
import {
  closeScopedAiPanel,
  setScopedAiContext,
  toggleScopedAiPanel,
} from '@/shared/store/uiSlice';
import { PageSpinner } from '@/shared/components/PageSpinner';
import {
  useGetResourceByIdQuery,
  useUpdateResourceMutation,
} from '@/features/resources/resourcesApi';
import { useObjectPermissionState } from '@/shared/hooks/useCanEdit';
import { useGoogleDocsExport } from '@/features/resources/hooks/useGoogleDocsExport';
import {
  uploadFile,
  extFromMime,
  resolveSignedUrl,
  PASTED_IMAGES_MAX_SIZE,
} from '@/shared/services/storageService';
import { resizeImageBlob } from '@/shared/utils/imageResize';
import {
  addAiDraftEditorFlushListener,
  addAiDraftResourceAppliedListener,
} from '@/features/chatbot/aiDraftApplyEvents';
import { isFeatureEnabled } from '@/shared/libs/featureFlags';
import { ScoperAiDialog } from '@/features/chatbot/scoper-dev';

// ---------------------------------------------------------------------------
// Image dimension helpers
// ---------------------------------------------------------------------------

/** Load an image URL and return its natural pixel dimensions. */
function loadImageNaturalSize(src: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth || 300, h: img.naturalHeight || 200 });
    img.onerror = () => resolve({ w: 300, h: 200 });
    img.src = src;
  });
}

/** Scale natural dimensions to fit within 60 % of the slide canvas. */
function fitToCanvas(naturalW: number, naturalH: number): { width: number; height: number } {
  const maxW = SLIDE_PAGE_WIDTH * 0.6;
  const maxH = SLIDE_PAGE_HEIGHT * 0.6;
  const scale = Math.min(maxW / naturalW, maxH / naturalH, 1);
  return { width: Math.round(naturalW * scale), height: Math.round(naturalH * scale) };
}

/** Given a new image aspect ratio and an existing element, return width/height
 *  that keeps roughly the same visual area while matching the new ratio. */
function reframeToRatio(
  existing: { width: number; height: number },
  naturalW: number,
  naturalH: number
): { width: number; height: number } {
  const ratio = naturalW / naturalH;
  const newH = Math.round(Math.sqrt((existing.width * existing.height) / ratio));
  const newW = Math.round(newH * ratio);
  return { width: newW, height: newH };
}

const ResourceSlideEditorPage: React.FC = () => {
  const { resourceId } = useParams<{ resourceId: string }>();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((s) => s.auth);

  // -- data loading ---------------------------------------------------------
  const {
    data: resource,
    isLoading,
    error,
  } = useGetResourceByIdQuery(resourceId!, { skip: !resourceId });
  const isOwner = Boolean(resource && user && resource.ownerId === user.id);
  const resourcePermission = useObjectPermissionState('resource', resourceId, 'editor', {
    fallbackAllowed: isOwner,
  });
  const canEdit = resourcePermission.isAllowed;
  const [updateResource, { isLoading: isSaving }] = useUpdateResourceMutation();

  // -- local state ----------------------------------------------------------
  const [title, setTitle] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [googleExportError, setGoogleExportError] = useState<string | null>(null);
  const [isGoogleExporting, setIsGoogleExporting] = useState(false);
  const [presenting, setPresenting] = useState(false);
  const videoPlaybackRegistry = useCreateVideoPlaybackRegistry();
  const { exportToGoogle } = useGoogleDocsExport({ onError: setGoogleExportError });
  const scoperEnabled = import.meta.env.DEV && isFeatureEnabled('scoperAiDevDialog');
  const accessibilityPreflightEnabled = isFeatureEnabled('googleSlidesAccessibilityPreflight');
  const [scoperOpen, setScoperOpen] = useState(false);

  // In-memory slide content — the single source of truth while the editor is open.
  const [slideContent, setSlideContent] = useState<SlideContentV1 | null>(null);

  // Version tracking (same pattern as document editor)
  const lastLoadedResourceRef = useRef<string | null>(null);
  const lastLoadedVersionRef = useRef<number>(0);
  const lastLoadedUpdatedAtRef = useRef<string | null>(null);
  const pendingAiAppliedDraftRef = useRef<string | null>(null);

  // -- undo / redo ----------------------------------------------------------
  const { pushSnapshot, undo, redo, canUndo, canRedo, resetHistory } =
    useSlideHistory(slideContent);

  // -- editor slice selectors -----------------------------------------------
  const currentSlideId = useAppSelector((s) => s.slideEditor.currentSlideId);
  const clipboard = useAppSelector((s) => s.slideEditor.clipboard);
  const clipboardRef = useRef<SlideElement[] | null>(null);
  clipboardRef.current = clipboard;
  const librarySelectInFlightRef = useRef(false);

  // -- derived data ---------------------------------------------------------
  const presentation = slideContent?.presentation ?? null;
  const theme = presentation?.theme ?? DEFAULT_THEME;
  const slides = useMemo(() => presentation?.slides ?? [], [presentation?.slides]);
  const currentSlide = useMemo(
    () => slides.find((s) => s.id === currentSlideId) ?? null,
    [slides, currentSlideId]
  );

  // -- accessibility checks -------------------------------------------------
  const currentSlideIssues = useMemo<AccessibilityIssue[]>(
    () => (canEdit && currentSlide ? checkSlide(resolveSlideForChecker(currentSlide, theme)) : []),
    [canEdit, currentSlide, theme]
  );

  // -- scope for AI panel ---------------------------------------------------
  const scopedLevel: 'course' | 'unit' | 'lesson' | undefined = resource?.parentLessonId
    ? 'lesson'
    : resource?.parentUnitId
      ? 'unit'
      : resource?.parentCourseId
        ? 'course'
        : undefined;
  const scoperContext = useMemo<AgentContext | null>(() => {
    if (!resource?.courseId) return null;

    return {
      assistantType: 'course',
      courseId: resource.courseId,
      focus: {
        level: scopedLevel ?? 'course',
        courseId: resource.courseId,
        unitId: resource.parentUnitId ?? undefined,
        lessonId: resource.parentLessonId ?? undefined,
        resourceId: resource.id,
        resourceKind: resource.resourceKind,
      },
      profile: resource.resourceKind === 'slide' ? 'slides' : undefined,
    };
  }, [resource, scopedLevel]);

  // -- content mutation callback (wired to hooks) ---------------------------
  const mutateContent = useCallback(
    (updater: (draft: SlideContentV1) => void) => {
      setSlideContent((prev) => {
        if (!prev) return prev;
        // Push snapshot for undo before applying the mutation
        pushSnapshot(prev, title);
        // Deep clone, mutate, return
        const next = structuredClone(prev);
        updater(next);
        return next;
      });
      setDirty(true);
    },
    [pushSnapshot, title]
  );

  // -- element hook ---------------------------------------------------------
  const slideElements = useSlideElements({ currentSlide, theme, mutateContent });
  const slideElementsRef = useRef(slideElements);
  slideElementsRef.current = slideElements;

  const applyAccessibilityFix = useCallback(
    (issue: AccessibilityIssue, fix: AccessibilityFix) => {
      if (!canEdit) return;
      const el = slideElements.elements.find((e) => e.id === issue.elementId);
      if (!el) return;
      switch (fix.kind) {
        case 'set-text-color':
          if (el.type !== 'text') return;
          slideElements.updateElement(el.id, { style: { ...el.style, color: fix.color } });
          return;
        case 'add-text-panel':
          if (el.type !== 'text') return;
          slideElements.updateElement(el.id, {
            style: { ...el.style, backgroundColor: fix.backgroundColor, padding: fix.padding },
          });
          return;
        case 'set-font-size':
          if (el.type !== 'text') return;
          slideElements.updateElement(el.id, { style: { ...el.style, fontSize: fix.fontSize } });
          return;
        case 'set-shape-stroke':
          if (el.type !== 'shape') return;
          slideElements.updateElement(el.id, {
            content: { ...el.content, strokeColor: fix.color },
          });
          return;
      }
    },
    [canEdit, slideElements]
  );

  // -- load content from resource -------------------------------------------
  useEffect(() => {
    if (!resource) return;
    const isInitialLoad = lastLoadedResourceRef.current !== resource.id;
    const resourceVersion = resource.version ?? 0;
    const resourceUpdatedAt = resource.updatedAt ?? null;
    const isServerUpdate =
      !isInitialLoad &&
      !dirty &&
      (resourceVersion > lastLoadedVersionRef.current ||
        isLaterTimestamp(resourceUpdatedAt, lastLoadedUpdatedAtRef.current));

    if (!isInitialLoad && !isServerUpdate) return;

    lastLoadedResourceRef.current = resource.id;
    lastLoadedVersionRef.current = resourceVersion;
    lastLoadedUpdatedAtRef.current = resourceUpdatedAt;
    setTitle(getSlideResourceTitle(resource));
    setSaveError(null);

    const normalizedContent =
      asSlideContent(resource) ??
      createEmptySlideContent(resource.title || 'Untitled Presentation');
    const isAiAppliedUpdate = isServerUpdate && pendingAiAppliedDraftRef.current !== null;
    if (isAiAppliedUpdate) {
      if (slideContent) {
        pushSnapshot(slideContent, title);
      }
      pendingAiAppliedDraftRef.current = null;
    }

    setSlideContent(normalizedContent);
    if (!isAiAppliedUpdate) {
      resetHistory();
    }
    setDirty(false);
  }, [resource, dirty, resetHistory, pushSnapshot, slideContent, title]);

  // Keep the selection valid whenever the loaded presentation changes.
  useEffect(() => {
    if (slides.length === 0) {
      if (currentSlideId !== null) {
        dispatch(setCurrentSlide(null));
      }
      return;
    }

    if (currentSlideId && slides.some((slide) => slide.id === currentSlideId)) {
      return;
    }

    dispatch(setCurrentSlide(slides[0].id));
  }, [slides, currentSlideId, dispatch]);

  const persistSlides = useCallback(
    async (force = false) => {
      if (!canEdit) return false;
      if (!resource || !slideContent) return false;
      if (!force && !dirty) return false;
      const nextTitle = title.trim() || 'Untitled Presentation';

      const nextContent: SlideContentV1 = {
        ...slideContent,
        presentation: {
          ...slideContent.presentation,
          title: nextTitle,
          // Embed the default theme on write if the deck has none (§9); never
          // overwrite an existing theme.
          theme: slideContent.presentation.theme ?? structuredClone(DEFAULT_THEME),
          slides: slideContent.presentation.slides.map((slide) => ({
            ...slide,
            elements: (slide.elements ?? []).map((element) => {
              if (element.type !== 'image' || !element.content.storagePath) return element;
              const nextElementContent = { ...element.content };
              delete nextElementContent.src;
              return { ...element, content: nextElementContent };
            }),
          })),
        },
      };

      try {
        setSaveError(null);
        const newVersion = (resource.version ?? 1) + 1;
        const savedResource = await updateResource({
          id: resource.id,
          title: nextTitle,
          content: nextContent,
          version: newVersion,
        }).unwrap();
        lastLoadedResourceRef.current = savedResource.id;
        lastLoadedVersionRef.current = savedResource.version ?? newVersion;
        lastLoadedUpdatedAtRef.current = savedResource.updatedAt ?? null;
        setTitle(getSlideResourceTitle(savedResource));
        setDirty(false);
        return true;
      } catch (err) {
        console.error('Failed to save slide resource', err);
        setSaveError('Unable to save changes. Keep this tab open and try again.');
        return false;
      }
    },
    [canEdit, resource, slideContent, dirty, title, updateResource]
  );

  useEffect(() => {
    if (!resourceId) return;

    return addAiDraftResourceAppliedListener((detail) => {
      if (detail.resourceIds.includes(resourceId)) {
        pendingAiAppliedDraftRef.current = detail.draftId;
      }
    });
  }, [resourceId]);

  useEffect(() => {
    if (!canEdit || !resourceId) return;

    return addAiDraftEditorFlushListener((detail) => {
      if (detail.resourceId !== resourceId || detail.resourceKind !== 'slide') return;
      detail.promises.push(dirty ? persistSlides(true) : Promise.resolve(true));
    });
  }, [canEdit, dirty, persistSlides, resourceId]);

  // -- autosave (900 ms debounce) -------------------------------------------
  useEffect(() => {
    if (!canEdit || !resource || !slideContent || !dirty) return;
    const handle = window.setTimeout(() => {
      void persistSlides();
    }, 900);
    return () => window.clearTimeout(handle);
  }, [canEdit, dirty, resource?.id, slideContent, persistSlides]);

  // -- undo / redo key bindings ---------------------------------------------
  useEffect(() => {
    if (!canEdit) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      if (!(event.metaKey || event.ctrlKey)) return;
      const key = event.key.toLowerCase();

      if (key === 's') {
        event.preventDefault();
        void persistSlides(true);
        return;
      }
      if (key === 'z' && !event.shiftKey) {
        event.preventDefault();
        if (slideContent) {
          const prev = undo(slideContent, title);
          if (prev) {
            setSlideContent(prev.content);
            setTitle(prev.title);
            setDirty(true);
          }
        }
        return;
      }
      if ((key === 'z' && event.shiftKey) || key === 'y') {
        event.preventDefault();
        if (slideContent) {
          const next = redo(slideContent, title);
          if (next) {
            setSlideContent(next.content);
            setTitle(next.title);
            setDirty(true);
          }
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [canEdit, slideContent, title, undo, redo, persistSlides]);

  // -- beforeunload guard ---------------------------------------------------
  useEffect(() => {
    if (!canEdit) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [canEdit, dirty]);

  const [preflightOpen, setPreflightOpen] = useState(false);
  const preflightIssues = useMemo<Record<string, AccessibilityIssue[]>>(
    () =>
      accessibilityPreflightEnabled && canEdit && preflightOpen && presentation
        ? checkDeck(presentation.slides.map((s) => resolveSlideForChecker(s, theme)))
        : {},
    [accessibilityPreflightEnabled, canEdit, preflightOpen, presentation, theme]
  );
  const preflightHasIssues = useMemo(
    () => Object.values(preflightIssues).some((list) => list.length > 0),
    [preflightIssues]
  );

  const runGoogleExport = useCallback(async () => {
    if (!resource || resource.resourceKind !== 'slide') return;

    if (canEdit && dirty) {
      const saved = await persistSlides(true);
      if (!saved) return;
    }

    setIsGoogleExporting(true);
    try {
      await exportToGoogle(resource);
    } finally {
      setIsGoogleExporting(false);
    }
  }, [canEdit, dirty, exportToGoogle, persistSlides, resource]);

  const handleExportToGoogleSlides = useCallback(async () => {
    if (!resource || resource.resourceKind !== 'slide') return;
    if (!canEdit || !accessibilityPreflightEnabled) {
      await runGoogleExport();
      return;
    }
    if (!presentation) return;
    const deckIssues = checkDeck(presentation.slides.map((s) => resolveSlideForChecker(s, theme)));
    const hasAny = Object.values(deckIssues).some((list) => list.length > 0);
    if (hasAny) {
      setPreflightOpen(true);
      return;
    }
    await runGoogleExport();
  }, [accessibilityPreflightEnabled, canEdit, resource, presentation, runGoogleExport, theme]);

  const handlePresent = useCallback(() => {
    void videoPlaybackRegistry.pauseAll().finally(() => setPresenting(true));
  }, [videoPlaybackRegistry]);

  // -- scoped AI context ----------------------------------------------------
  useEffect(() => {
    if (resourcePermission.isLoading) return;
    if (!canEdit) {
      dispatch(closeScopedAiPanel());
      return;
    }
    if (!resource) return;
    dispatch(
      setScopedAiContext({
        level: scopedLevel ?? 'course',
        courseId: resource.courseId,
        unitId: resource.parentUnitId ?? undefined,
        lessonId: resource.parentLessonId ?? undefined,
        resourceId: resource.id,
        resourceKind: resource.resourceKind,
      })
    );
  }, [canEdit, resourcePermission.isLoading, dispatch, resource, scopedLevel]);

  // -- cleanup on unmount ---------------------------------------------------
  useEffect(
    () => () => {
      dispatch(resetEditor());
    },
    [dispatch]
  );

  // -- slide management callbacks -------------------------------------------
  const handleAddSlide = useCallback(
    (template: SlideTemplate = 'BLANK') => {
      if (!slideContent) return;
      const id = crypto.randomUUID();
      // Bare placeholder elements; no background ⇒ inherits the layout background.
      const newSlide = {
        id,
        template,
        elements: getDefaultElements(template),
      };
      pushSnapshot(slideContent, title);
      setSlideContent((prev) => {
        if (!prev) return prev;
        const slides = [...prev.presentation.slides];
        const focusedIdx = currentSlideId ? slides.findIndex((s) => s.id === currentSlideId) : -1;
        const insertIdx = focusedIdx >= 0 ? focusedIdx + 1 : slides.length;
        slides.splice(insertIdx, 0, newSlide);
        return { ...prev, presentation: { ...prev.presentation, slides } };
      });
      dispatch(setCurrentSlide(id));
      setDirty(true);
    },
    [slideContent, title, currentSlideId, dispatch, pushSnapshot]
  );

  const handleDeleteSlide = useCallback(
    (slideId: string) => {
      if (!slideContent) return;
      if (slideContent.presentation.slides.length <= 1) return; // keep at least one slide
      pushSnapshot(slideContent, title);
      setSlideContent((prev) => {
        if (!prev) return prev;
        const next = prev.presentation.slides.filter((s) => s.id !== slideId);
        if (next.length === prev.presentation.slides.length) return prev; // nothing removed
        return { ...prev, presentation: { ...prev.presentation, slides: next } };
      });
      // Select neighbouring slide
      if (currentSlideId === slideId) {
        const slides = slideContent.presentation.slides;
        const idx = slides.findIndex((s) => s.id === slideId);
        const remaining = slides.filter((s) => s.id !== slideId);
        const newIdx = Math.min(idx, remaining.length - 1);
        dispatch(setCurrentSlide(remaining[newIdx]?.id ?? null));
      }
      setDirty(true);
    },
    [slideContent, title, currentSlideId, dispatch, pushSnapshot]
  );

  const handleReorderSlides = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (!slideContent || fromIndex === toIndex) return;
      pushSnapshot(slideContent, title);
      setSlideContent((prev) => {
        if (!prev) return prev;
        const slides = [...prev.presentation.slides];
        const [moved] = slides.splice(fromIndex, 1);
        slides.splice(toIndex, 0, moved);
        return { ...prev, presentation: { ...prev.presentation, slides } };
      });
      setDirty(true);
    },
    [slideContent, title, pushSnapshot]
  );

  // -- slide background callback --------------------------------------------
  const handleUpdateSlideBackground = useCallback(
    (color: string) => {
      if (!currentSlideId) return;
      const sid = currentSlideId;
      mutateContent((draft) => {
        const slide = draft.presentation.slides.find((s) => s.id === sid);
        if (slide) slide.background = { color };
      });
    },
    [currentSlideId, mutateContent]
  );

  // -- theme apply callback (§6.3) ------------------------------------------
  // A theme switch is one synchronous, undoable content change with no text-fit:
  // re-embed the freshly-resolved registry snapshot. Skip-when-identical (D1) so
  // re-selecting the active theme with no registry drift is a true no-op (no undo
  // entry, no autosave); a drifted snapshot differs and re-embeds.
  const handleApplyTheme = useCallback(
    (themeId: ThemeId) => {
      if (!slideContent) return;
      const next = registryThemeSnapshot(themeId);
      if (themesEqual(slideContent.presentation.theme, next)) return;
      mutateContent((draft) => {
        draft.presentation.theme = next;
      });
    },
    [slideContent, mutateContent]
  );

  // -- slide duplication callback -------------------------------------------
  const handleDuplicateSlide = useCallback(
    (slideId: string) => {
      if (!slideContent) return;
      const sourceSlide = slideContent.presentation.slides.find((s) => s.id === slideId);
      if (!sourceSlide) return;

      pushSnapshot(slideContent, title);
      const newId = crypto.randomUUID();
      const cloned = structuredClone(sourceSlide);
      cloned.id = newId;
      // Assign new IDs to all elements
      cloned.elements = (cloned.elements ?? []).map((el) => ({ ...el, id: crypto.randomUUID() }));

      setSlideContent((prev) => {
        if (!prev) return prev;
        const slides = [...prev.presentation.slides];
        const idx = slides.findIndex((s) => s.id === slideId);
        slides.splice(idx + 1, 0, cloned);
        return { ...prev, presentation: { ...prev.presentation, slides } };
      });
      dispatch(setCurrentSlide(newId));
      setDirty(true);
    },
    [slideContent, title, dispatch, pushSnapshot]
  );

  // -- image URL dialog state -----------------------------------------------
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageDialogPosition, setImageDialogPosition] = useState<{ x: number; y: number }>({
    x: 200,
    y: 200,
  });
  const [editingImageElementId, setEditingImageElementId] = useState<string | null>(null);

  // -- image library dialog state -------------------------------------------
  const [imageLibraryOpen, setImageLibraryOpen] = useState(false);
  const [imageLibrarySearchTerm, setImageLibrarySearchTerm] = useState('');
  const [autoAdvancePending, setAutoAdvancePending] = useState(false);

  // -- video library dialog state -------------------------------------------
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [videoDialogInitialTab, setVideoDialogInitialTab] = useState<'search' | 'url'>('search');

  const handleImageDialogOpen = useCallback((x: number, y: number) => {
    setImageDialogPosition({ x, y });
    setEditingImageElementId(null);
    setImageDialogOpen(true);
  }, []);

  /** Double-click on an image element: unresolved → library, resolved → URL dialog */
  const handleImageElementEdit = useCallback(
    (elementId: string) => {
      const el = slideElements.elements.find((e) => e.id === elementId && e.type === 'image') as
        | ImageElement
        | undefined;
      if (!el) return;
      setEditingImageElementId(elementId);
      if (!el.content.src && el.content.imageQuery) {
        // Unresolved placeholder → open library pre-searched
        setImageLibrarySearchTerm(el.content.imageQuery);
        setImageLibraryOpen(true);
      } else {
        // Resolved image → open URL dialog for editing
        setImageDialogOpen(true);
      }
    },
    [slideElements.elements]
  );

  const handleImageDialogConfirm = useCallback(
    async (src: string, alt: string) => {
      const { w, h } = await loadImageNaturalSize(src);

      if (editingImageElementId) {
        const existing = slideElements.elements.find((e) => e.id === editingImageElementId);
        if (existing) {
          const dims = reframeToRatio(slideElements.geometryOf(existing), w, h);
          slideElements.updateElement(editingImageElementId, {
            content: { src, alt },
            position: { width: dims.width, height: dims.height },
          } as Partial<ImageElement>);
        }
      } else {
        const dims = fitToCanvas(w, h);
        slideElements.createImageElement(
          { src, alt, width: dims.width, height: dims.height },
          imageDialogPosition.x,
          imageDialogPosition.y
        );
      }
      setImageDialogOpen(false);
      setEditingImageElementId(null);
    },
    [editingImageElementId, slideElements, imageDialogPosition]
  );

  const editingImageElement = editingImageElementId
    ? (slideElements.elements.find(
        (el) => el.id === editingImageElementId && el.type === 'image'
      ) as ImageElement | undefined)
    : undefined;

  /** Toolbar "Search Library" button — opens library with empty search */
  const handleInsertImageFromLibrary = useCallback(() => {
    setEditingImageElementId(null);
    setImageLibrarySearchTerm('');
    setAutoAdvancePending(false);
    setImageLibraryOpen(true);
  }, []);

  /** Toolbar "Insert video" → opens the video library dialog on the chosen tab. */
  const handleInsertVideo = useCallback((mode: 'search' | 'url') => {
    setVideoDialogInitialTab(mode);
    setVideoDialogOpen(true);
  }, []);

  const handleVideoSelect = useCallback(
    (selection: VideoSelection) => {
      slideElements.createVideoElement({
        videoId: selection.videoId,
        title: selection.title,
        channelName: selection.channelName,
        thumbnailUrl: selection.thumbnailUrl,
      });
      setVideoDialogOpen(false);
    },
    [slideElements]
  );

  // -- pending images (across ALL slides) -----------------------------------
  const pendingImageCount = useMemo(() => {
    if (!slideContent) return 0;
    let count = 0;
    for (const slide of slideContent.presentation.slides) {
      for (const el of slide.elements ?? []) {
        if (
          el.type === 'image' &&
          !el.content.src &&
          !el.content.storagePath &&
          el.content.imageQuery
        )
          count++;
      }
    }
    return count;
  }, [slideContent]);

  /** Find the next unresolved image element across all slides. */
  const findNextPendingImage = useCallback((): {
    slideId: string;
    elementId: string;
    imageQuery: string;
  } | null => {
    if (!slideContent) return null;
    for (const slide of slideContent.presentation.slides) {
      for (const el of slide.elements ?? []) {
        if (
          el.type === 'image' &&
          !el.content.src &&
          !el.content.storagePath &&
          el.content.imageQuery
        ) {
          return { slideId: slide.id, elementId: el.id, imageQuery: el.content.imageQuery };
        }
      }
    }
    return null;
  }, [slideContent]);

  /** Navigate to the next unresolved image and open the library. */
  const handleReviewPendingImages = useCallback(() => {
    const next = findNextPendingImage();
    if (!next) return;
    dispatch(setCurrentSlide(next.slideId));
    dispatch(selectElements([next.elementId]));
    setEditingImageElementId(next.elementId);
    setImageLibrarySearchTerm(next.imageQuery);
    setAutoAdvancePending(true);
    setImageLibraryOpen(true);
  }, [findNextPendingImage, dispatch]);

  /** Handle image selected from the library dialog. */
  const handleLibraryImageSelect = useCallback(
    async (selection: UnsplashImageSelection) => {
      if (librarySelectInFlightRef.current) return;
      // Auto-advance gap guard: in keepOpenOnSelect mode there's a render between
      // setEditingImageElementId(null) and the advance effect picking the next
      // placeholder. A click in that gap would otherwise fall through to
      // createImageElement and produce a stray image.
      if (autoAdvancePending && !editingImageElementId) return;
      librarySelectInFlightRef.current = true;
      try {
        const contentUpdate: Partial<ImageElement['content']> = {
          src: selection.src,
          alt: selection.alt,
        };
        if (selection.storageBucket) contentUpdate.storageBucket = selection.storageBucket;
        if (selection.storagePath) contentUpdate.storagePath = selection.storagePath;

        const { w, h } = await loadImageNaturalSize(selection.src);

        if (editingImageElementId) {
          const existing = slideElements.elements.find((e) => e.id === editingImageElementId);
          if (existing) {
            const dims = reframeToRatio(slideElements.geometryOf(existing), w, h);
            slideElements.updateElement(editingImageElementId, {
              content: contentUpdate,
              position: { width: dims.width, height: dims.height },
            } as Partial<ImageElement>);
          }
        } else {
          const dims = fitToCanvas(w, h);
          slideElements.createImageElement({
            ...contentUpdate,
            width: dims.width,
            height: dims.height,
          });
        }
        setEditingImageElementId(null);

        if (!autoAdvancePending) {
          setImageLibraryOpen(false);
        }
      } finally {
        librarySelectInFlightRef.current = false;
      }
    },
    [editingImageElementId, slideElements, autoAdvancePending]
  );

  // Auto-advance to next unresolved placeholder after the resolved one leaves
  // pendingImageCount. This runs on fresh state (no stale closure).
  useEffect(() => {
    if (!autoAdvancePending || !imageLibraryOpen) return;
    // editingImageElementId is null right after a selection — time to advance
    if (editingImageElementId !== null) return;

    const next = findNextPendingImage();
    if (next) {
      dispatch(setCurrentSlide(next.slideId));
      dispatch(selectElements([next.elementId]));
      setEditingImageElementId(next.elementId);
      setImageLibrarySearchTerm(next.imageQuery);
    } else {
      setImageLibraryOpen(false);
      setAutoAdvancePending(false);
    }
  }, [
    autoAdvancePending,
    imageLibraryOpen,
    editingImageElementId,
    pendingImageCount,
    findNextPendingImage,
    dispatch,
  ]);

  // -- keyboard shortcuts (Delete, Copy, Paste, Cut, Tool, Format) ----------
  useEffect(() => {
    if (!canEdit) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const tag = (event.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (event.target as HTMLElement)?.isContentEditable)
        return;

      const currentSlideElements = slideElementsRef.current;
      const selectedIds = currentSlideElements.selectedElementIds;
      const isMod = event.metaKey || event.ctrlKey;
      const key = event.key.toLowerCase();

      // Delete / Backspace
      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedIds.length > 0) {
        event.preventDefault();
        currentSlideElements.removeElements(selectedIds);
        return;
      }

      // --- Tool shortcuts (no modifier) ---
      if (!isMod && !event.altKey && !event.shiftKey) {
        if (key === 'v') {
          dispatch(setActiveTool('select'));
          return;
        }
        if (key === 't') {
          dispatch(setActiveTool('text'));
          return;
        }
      }

      // --- Modifier shortcuts ---
      if (!isMod) return;

      // Ctrl/Cmd+A — Select all elements
      if (key === 'a') {
        event.preventDefault();
        const allIds = slideElementsRef.current.elements.map((el) => el.id);
        if (allIds.length > 0) {
          dispatch(selectElements(allIds));
          dispatch(setActiveTool('select'));
        }
        return;
      }

      // Ctrl/Cmd+D — Duplicate
      if (key === 'd' && selectedIds.length === 1) {
        event.preventDefault();
        currentSlideElements.duplicateElement(selectedIds[0]);
        return;
      }

      // Ctrl/Cmd+B — Bold (read current state from the resolved view; write the
      // stored sparse style as base so other fields keep inheriting — §8.2/§10.9).
      if (key === 'b' && selectedIds.length === 1) {
        const el = currentSlideElements.selectedElement;
        const resolved = currentSlideElements.selectedElementResolved;
        if (el?.type === 'text' && resolved?.type === 'text') {
          event.preventDefault();
          const isBold =
            resolved.style.fontWeight === 'bold' || resolved.style.fontWeight === '700';
          currentSlideElements.updateElement(el.id, {
            style: { ...(el.style ?? {}), fontWeight: isBold ? 'normal' : 'bold' },
          });
          return;
        }
      }

      // Ctrl/Cmd+I — Italic
      if (key === 'i' && selectedIds.length === 1) {
        const el = currentSlideElements.selectedElement;
        const resolved = currentSlideElements.selectedElementResolved;
        if (el?.type === 'text' && resolved?.type === 'text') {
          event.preventDefault();
          const isItalic = resolved.style.fontStyle === 'italic';
          currentSlideElements.updateElement(el.id, {
            style: { ...(el.style ?? {}), fontStyle: isItalic ? 'normal' : 'italic' },
          });
          return;
        }
      }

      // Ctrl/Cmd+U — Underline
      if (key === 'u' && selectedIds.length === 1) {
        const el = currentSlideElements.selectedElement;
        const resolved = currentSlideElements.selectedElementResolved;
        if (el?.type === 'text' && resolved?.type === 'text') {
          event.preventDefault();
          const isUnderline = resolved.style.textDecoration === 'underline';
          currentSlideElements.updateElement(el.id, {
            style: { ...(el.style ?? {}), textDecoration: isUnderline ? 'none' : 'underline' },
          });
          return;
        }
      }

      // Ctrl/Cmd+C — Copy
      if (key === 'c' && !event.shiftKey && selectedIds.length > 0) {
        event.preventDefault();
        const elsToCopy = currentSlideElements.elements.filter((el) => selectedIds.includes(el.id));
        dispatch(copyToClipboard(elsToCopy));
        return;
      }

      // Ctrl/Cmd+X — Cut
      if (key === 'x' && selectedIds.length > 0) {
        event.preventDefault();
        const elsToCopy = currentSlideElements.elements.filter((el) => selectedIds.includes(el.id));
        dispatch(copyToClipboard(elsToCopy));
        currentSlideElements.removeElements(selectedIds);
        return;
      }

      // Ctrl/Cmd+V — Paste
      if (key === 'v') {
        const clipboardEls = clipboardRef.current;
        if (!clipboardEls || clipboardEls.length === 0) return;
        event.preventDefault();
        const newIds: string[] = [];
        const clonedElements: Parameters<typeof currentSlideElements.addElements>[0] = [];
        for (const el of clipboardEls) {
          // Paste as a free element: resolve geometry, strip the placeholder
          // binding, and offset (§8.2 — same rule as duplicate).
          const basePos = currentSlideElements.geometryOf(el);
          const cloned = structuredClone(el);
          cloned.id = crypto.randomUUID();
          if (cloned.type === 'text') delete cloned.placeholderId;
          cloned.position = { ...basePos, x: basePos.x + 20, y: basePos.y + 20 };
          clonedElements.push(cloned);
          newIds.push(cloned.id);
        }
        currentSlideElements.addElements(clonedElements);
        if (newIds.length > 0) dispatch(selectElements(newIds));
        return;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [canEdit, dispatch]);

  // -- image paste from clipboard -------------------------------------------
  useEffect(() => {
    if (!canEdit || !user || !resourceId) return;

    const onPaste = async (event: ClipboardEvent) => {
      const tag = (event.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (event.target as HTMLElement)?.isContentEditable)
        return;

      const items = event.clipboardData?.items;
      if (!items) return;
      let imageItem: DataTransferItem | null = null;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          imageItem = item;
          break;
        }
      }
      if (!imageItem) return;

      event.preventDefault();
      const blob = imageItem.getAsFile();
      if (!blob) return;
      if (blob.size > PASTED_IMAGES_MAX_SIZE) return;

      try {
        const resized = await resizeImageBlob(blob);
        const ext = extFromMime(resized.type || blob.type);
        const { storageBucket, storagePath } = await uploadFile(
          'pasted-images',
          user.id,
          resourceId,
          resized,
          ext
        );
        const signedUrl = await resolveSignedUrl(storageBucket, storagePath);

        // Determine natural size for reasonable default placement
        const imgEl = new Image();
        imgEl.src = signedUrl;
        await new Promise<void>((resolve) => {
          imgEl.onload = () => resolve();
          imgEl.onerror = () => resolve();
        });
        const naturalW = imgEl.naturalWidth || 400;
        const naturalH = imgEl.naturalHeight || 300;
        const maxW = SLIDE_PAGE_WIDTH * 0.6;
        const maxH = SLIDE_PAGE_HEIGHT * 0.6;
        const scale = Math.min(maxW / naturalW, maxH / naturalH, 1);
        const w = Math.round(naturalW * scale);
        const h = Math.round(naturalH * scale);

        slideElementsRef.current.createImageElement(
          {
            src: signedUrl,
            alt: '',
            storageBucket,
            storagePath,
            width: w,
            height: h,
          },
          Math.round((SLIDE_PAGE_WIDTH - w) / 2),
          Math.round((SLIDE_PAGE_HEIGHT - h) / 2)
        );
      } catch (err) {
        console.error('Slide image paste upload failed', err);
      }
    };

    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [canEdit, user, resourceId]);

  // -- render ---------------------------------------------------------------

  if (isLoading) return <PageSpinner />;
  if (error || !resource) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity={!user ? 'warning' : 'error'}>
          {!user ? (
            <>
              This content may be private. <Link to="/login">Sign in</Link> to access it if you have
              permission.
            </>
          ) : (
            'Unable to load the slide resource.'
          )}
        </Alert>
      </Box>
    );
  }

  if (resource.resourceKind !== 'slide') {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">This resource is not a slide deck.</Alert>
      </Box>
    );
  }

  return (
    <VideoPlaybackRegistryProvider value={videoPlaybackRegistry}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* ---- header ---- */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1,
            gap: 1,
          }}
        >
          <TextField
            label="Title"
            size="small"
            sx={{ maxWidth: 540, minWidth: 300 }}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setDirty(true);
            }}
            slotProps={{ input: { readOnly: !canEdit } }}
          />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {!canEdit && <Chip label="View only" size="small" variant="outlined" />}
            {canEdit && isSaving && (
              <Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>
                Saving…
              </Box>
            )}
            {canEdit && (
              <>
                <Button
                  variant="contained"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => dispatch(toggleScopedAiPanel())}
                >
                  AI CoPlanner
                </Button>
                {scoperEnabled && scoperContext && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => setScoperOpen(true)}
                    sx={{ borderStyle: 'dashed' }}
                  >
                    Dev AI
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>

        {saveError && (
          <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
            {saveError}
          </Alert>
        )}
        {googleExportError && (
          <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
            {googleExportError}
          </Alert>
        )}

        {scoperEnabled && scoperContext && (
          <ScoperAiDialog
            open={scoperOpen}
            onClose={() => setScoperOpen(false)}
            context={scoperContext}
          />
        )}

        {/* ---- toolbar ---- */}
        <SlideToolbar
          slideElements={slideElements}
          canEdit={canEdit}
          canUndo={canUndo}
          canRedo={canRedo}
          onAddSlide={handleAddSlide}
          onApplyTheme={handleApplyTheme}
          activeThemeId={theme.themeId}
          onInsertInteractive={(widgetType) => slideElements.createInteractiveElement(widgetType)}
          onInsertImageFromLibrary={handleInsertImageFromLibrary}
          onInsertVideo={handleInsertVideo}
          pendingImageCount={pendingImageCount}
          onReviewPendingImages={handleReviewPendingImages}
          slideBackgroundColor={
            currentSlide ? resolveSlideBackground(currentSlide, theme) : undefined
          }
          onUpdateSlideBackground={handleUpdateSlideBackground}
          onGoogleExport={user ? handleExportToGoogleSlides : undefined}
          googleExporting={isGoogleExporting}
          onPresent={handlePresent}
          onUndo={() => {
            if (slideContent) {
              const prev = undo(slideContent, title);
              if (prev) {
                setSlideContent(prev.content);
                setTitle(prev.title);
                setDirty(true);
              }
            }
          }}
          onRedo={() => {
            if (slideContent) {
              const next = redo(slideContent, title);
              if (next) {
                setSlideContent(next.content);
                setTitle(next.title);
                setDirty(true);
              }
            }
          }}
        />

        {/* ---- main area: thumbnail panel + canvas ---- */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          <SlideThumbnailPanel
            slides={slides}
            theme={theme}
            currentSlideId={currentSlideId}
            canEdit={canEdit}
            onSelectSlide={(id: string) => dispatch(setCurrentSlide(id))}
            onAddSlide={handleAddSlide}
            onDeleteSlide={handleDeleteSlide}
            onReorderSlides={handleReorderSlides}
            onDuplicateSlide={handleDuplicateSlide}
          />
          <Box sx={{ flex: 1, display: 'flex', position: 'relative', overflow: 'hidden' }}>
            <SlideCanvas
              currentSlide={currentSlide}
              theme={theme}
              slideElements={slideElements}
              canEdit={canEdit}
              onImageToolClick={handleImageDialogOpen}
              onImageDoubleClick={handleImageElementEdit}
              suppressVideoPreview={presenting}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10,
                pointerEvents: 'none',
                '& > *': { pointerEvents: 'auto' },
              }}
            >
              <AccessibilityChip
                issues={currentSlideIssues}
                onApplyFix={canEdit ? applyAccessibilityFix : undefined}
                onSelectElement={(id) => slideElements.selectElement(id)}
              />
            </Box>
          </Box>
        </Box>

        {/* Presentation overlay */}
        {presenting && slides.length > 0 && (
          <PresentationOverlay
            slides={slides}
            theme={theme}
            startSlideIndex={Math.max(
              0,
              slides.findIndex((s) => s.id === currentSlideId)
            )}
            onExit={() => setPresenting(false)}
          />
        )}

        {/* Image URL dialog */}
        <ImageUrlDialog
          open={imageDialogOpen}
          initialSrc={editingImageElement?.content.src ?? ''}
          initialAlt={editingImageElement?.content.alt ?? ''}
          onConfirm={handleImageDialogConfirm}
          onCancel={() => {
            setImageDialogOpen(false);
            setEditingImageElementId(null);
          }}
        />

        {/* Accessibility preflight dialog (shown when export finds issues) */}
        <AccessibilityPreflightDialog
          open={accessibilityPreflightEnabled && preflightOpen && preflightHasIssues}
          slideOrder={slides.map((s, idx) => ({ id: s.id, index: idx }))}
          issuesBySlide={preflightIssues}
          onReview={(slideId) => {
            dispatch(setCurrentSlide(slideId));
            setPreflightOpen(false);
          }}
          onExportAnyway={() => {
            setPreflightOpen(false);
            void runGoogleExport();
          }}
          onCancel={() => setPreflightOpen(false)}
        />

        {/* Image library dialog (multi-provider search) */}
        <UnsplashImageLibraryDialog
          open={imageLibraryOpen}
          onClose={() => {
            setImageLibraryOpen(false);
            setEditingImageElementId(null);
            setAutoAdvancePending(false);
          }}
          onSelectImage={handleLibraryImageSelect}
          initialSearchTerm={imageLibrarySearchTerm}
          keepOpenOnSelect={autoAdvancePending}
          uploadContext={user && resourceId ? { userId: user.id, resourceId } : undefined}
        />

        {/* Video library dialog (YouTube search + paste URL) */}
        <VideoLibraryDialog
          open={videoDialogOpen}
          onClose={() => setVideoDialogOpen(false)}
          onSelectVideo={handleVideoSelect}
          initialTab={videoDialogInitialTab}
        />
      </Box>
    </VideoPlaybackRegistryProvider>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getSlideResourceTitle(resource: Resource): string {
  const resourceTitle = typeof resource.title === 'string' ? resource.title.trim() : '';
  if (resourceTitle) return resourceTitle;

  if (resource.resourceKind === 'slide') {
    const normalized = asSlideContent(resource);
    const presentationTitle = normalized?.presentation.title.trim() ?? '';
    if (presentationTitle) return presentationTitle;
  }

  return 'Untitled Presentation';
}

function isLaterTimestamp(nextValue: string | null, previousValue: string | null): boolean {
  if (!nextValue) return false;
  if (!previousValue) return true;

  const nextTime = Date.parse(nextValue);
  const previousTime = Date.parse(previousValue);

  if (Number.isNaN(nextTime) || Number.isNaN(previousTime)) {
    return nextValue > previousValue;
  }

  return nextTime > previousTime;
}

export default ResourceSlideEditorPage;
